import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { CaseStudyPageCard } from '@/components/CaseStudyPageCard';
import { SpotifyResultsSection } from '@/components/SpotifyResultsSection';
import { TestimonialsSection } from '@/components/TestimonialsSection';
import { CaseStudiesFAQ } from '@/components/CaseStudiesFAQ';
import { caseStudiesPageData } from '@/data/caseStudiesPageData';
import { TypedWord } from '@/components/TypedWord';
import { ScrollController } from '@/components/ScrollController';
import { SimpleReveal } from '@/components/SimpleReveal';
import { ChevronDown } from 'lucide-react';

const CaseStudies = () => {
  return (
    <>
      <Helmet>
        <title>Case Studies | DK Artists - Real Artist Growth Stories</title>
        <meta 
          name="description" 
          content="See how we've helped artists achieve millions of streams, viral moments, and sustainable careers. Real results from real artists." 
        />
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        <Navigation />
        <ScrollController>
          {/* Header Section */}
          <section className="pt-32 lg:pt-40 pb-6 lg:pb-8 relative" data-nav-theme="dark">
            {/* Grid Pattern */}
            <div 
              className="absolute inset-0 grid-pattern pointer-events-none" 
              style={{
                maskImage: 'radial-gradient(ellipse 100% 80% at 50% 30%, black 0%, rgba(0,0,0,0.4) 50%, transparent 85%)',
                WebkitMaskImage: 'radial-gradient(ellipse 100% 80% at 50% 30%, black 0%, rgba(0,0,0,0.4) 50%, transparent 85%)',
              }}
            />
            
            {/* Ambient Glow */}
            <div 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] lg:w-[900px] lg:h-[500px] rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(0,113,227,0.12) 0%, rgba(0,113,227,0.05) 40%, transparent 70%)',
                filter: 'blur(60px)',
              }}
            />
            
            <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
              {/* Badge */}
              <SimpleReveal delay={60}>
                <div className="flex justify-center mb-4">
                  <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
                    <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
                      &gt; The Proof
                    </span>
                  </div>
                </div>
              </SimpleReveal>

              {/* Headline */}
              <SimpleReveal delay={120}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 tracking-tight">
                  Artist Success{' '}
                  <span className="block md:inline">
                    <TypedWord 
                      words={["Stories", "Proof", "Results"]} 
                      className="text-[#0071e3]"
                      typingSpeed={80}
                      pauseDuration={2500}
                    />
                  </span>
                </h1>
              </SimpleReveal>

              {/* Subheader */}
              <SimpleReveal delay={180}>
                <p className="body-large text-white/60 max-w-2xl mx-auto">
                  These aren't hypotheticals. These are real artists who built real momentum with our systems.
                </p>
              </SimpleReveal>

              {/* Scroll Indicator */}
              <SimpleReveal delay={240}>
                <div className="flex justify-center mt-8 lg:mt-12">
                  <ChevronDown className="w-6 h-6 text-white/40 hover:text-white/60 transition-colors duration-300" />
                </div>
              </SimpleReveal>
            </div>
          </section>

          {/* Case Studies */}
          <section className="relative" data-nav-theme="dark">
            {/* Grid Pattern */}
            <div className="absolute inset-0 grid-pattern pointer-events-none" />
            
            <div className="relative z-10">
              {caseStudiesPageData.map((study, index) => (
                <SimpleReveal key={study.id} delay={240 + index * 100}>
                  <CaseStudyPageCard 
                    study={study} 
                    index={index} 
                    isLast={index === caseStudiesPageData.length - 1} 
                  />
                </SimpleReveal>
              ))}
            </div>
          </section>

          {/* Spotify Results Section - TEMPORARILY HIDDEN (uncomment when data/screenshots ready) */}
          {/*
          <section className="relative" data-nav-theme="dark">
            <div className="absolute inset-0 grid-pattern pointer-events-none" />
            
            <div className="relative z-10">
              <SpotifyResultsSection />
            </div>
          </section>
          */}

          {/* Testimonials Section */}
          <SimpleReveal>
            <TestimonialsSection />
          </SimpleReveal>

          {/* FAQ Section */}
          <SimpleReveal>
            <CaseStudiesFAQ />
          </SimpleReveal>

          {/* Footer */}
          <Footer />
        </ScrollController>
      </div>
    </>
  );
};

export default CaseStudies;
